<template>
  <PreviewAlertCard
    :color="theme.color"
    :icon="theme.icon"
    :title="title"
    :learn-more-href="learnMoreHref"
  >
    {{ bodyText }}
    <template
      v-if="showActions"
      #footer
    >
      <div class="flsa-alert-actions d-flex align-end ga-4">
        <div class="flsa-alert-jurisdiction flex-grow-1">
          <label
            :for="jurisdictionSelectId"
            class="text-body-3 text-textDarkest d-block mb-1"
          >
            Jurisdiction
          </label>
          <v-select
            :id="jurisdictionSelectId"
            v-model="selectedFormUlid"
            :items="formSelectItems"
            item-title="title"
            item-value="value"
            density="compact"
            variant="outlined"
            rounded="lg"
            color="primary"
            menu-icon="$chevron-down"
            hide-details
            placeholder="Jurisdiction"
            class="flsa-jurisdiction-select text-body-2"
            :menu-props="{ contentClass: 'flsa-jurisdiction-select-menu' }"
          />
        </div>
        <HideRbac :permissions="['createFlsaSubmission']">
          <v-btn
            rounded="lg"
            color="primary"
            variant="flat"
            height="40"
            class="text-button-1 flex-shrink-0"
            :disabled="!realFormUlids.has(selectedFormUlid)"
            @click="handleCreateNew"
          >
            Create New
          </v-btn>
        </HideRbac>
      </div>
    </template>
  </PreviewAlertCard>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import PreviewAlertCard from '@/components/shared/PreviewAlertCard.vue';
import HideRbac from '@/components/rbac/HideRbac.vue';
import {
  alertShowsCreateActions,
  getFlsaAlertPreviewBody,
  getFlsaAlertPreviewLabel,
  getFlsaAlertPreviewLearnMoreHref,
  getFlsaAlertPreviewTheme,
} from '@/components/library/flsaSurveys/alertHelpers';
import {
  buildFlsaFormSelectItems,
  findEffectiveFlsaForm,
  getRealFlsaFormUlids,
  navigateToFlsaFormCreate,
} from '@/helpers/flsa';
import { useTracking } from '@/stores/tracking/tracking';

const props = defineProps({
  alertCode: {
    type: String,
    required: true,
  },
  latestFederalSubmission: {
    type: Object,
    default: null,
  },
  forms: {
    type: Array,
    default: () => [],
  },
  jobProfileUlid: {
    type: String,
    default: null,
  },
  draftJobProfileUlid: {
    type: String,
    default: null,
  },
});

const router = useRouter();

const selectedFormUlid = ref(null);

const jurisdictionSelectId = computed(() => `flsa-alert-jurisdiction-${props.alertCode}`);

const theme = computed(() => getFlsaAlertPreviewTheme(props.alertCode));

const title = computed(() => getFlsaAlertPreviewLabel(props.alertCode));

const bodyText = computed(() => getFlsaAlertPreviewBody({
  code: props.alertCode,
  latestSubmission: props.latestFederalSubmission,
}));

const learnMoreHref = computed(() => getFlsaAlertPreviewLearnMoreHref(props.alertCode));

const showActions = computed(() => alertShowsCreateActions(props.alertCode));

const formSelectItems = computed(() => buildFlsaFormSelectItems(props.forms));

const realFormUlids = computed(() => getRealFlsaFormUlids(props.forms));

const defaultFederalFormUlid = computed(() => (
  findEffectiveFlsaForm(props.forms, { jurisdiction: 'federal' })?.ulid || null
));

watch(
  () => [props.forms, defaultFederalFormUlid.value],
  () => {
    if (!selectedFormUlid.value && defaultFederalFormUlid.value) {
      selectedFormUlid.value = defaultFederalFormUlid.value;
    }
  },
  { immediate: true },
);

const handleCreateNew = () => {
  const selectedForm = props.forms.find((form) => form.ulid === selectedFormUlid.value);

  useTracking().flsaLibClickedCreateNew({
    jurisdiction: selectedForm?.jurisdiction || 'federal',
  });

  navigateToFlsaFormCreate({
    router,
    jobProfileUlid: props.jobProfileUlid,
    draftJobProfileUlid: props.draftJobProfileUlid,
    flsaFormUlid: selectedFormUlid.value,
    realFormUlids: realFormUlids.value,
  });
};
</script>

<style scoped lang="less">
.flsa-alert-actions {
  width: 100%;
}

.flsa-jurisdiction-select {
  :deep(.v-field__append-inner .v-icon) {
    color: @text-dark;
  }

  :deep(.v-field) {
    color: @text-darkest;
    background-color: @bg-default;
  }

  :deep(.v-field__overlay) {
    background-color: @bg-default;
    opacity: 1;
  }

  :deep(.v-select__selection--placeholder) {
    color: @text-dark;
  }
}
</style>

<style lang="less">
.flsa-jurisdiction-select-menu.v-overlay__content > .v-list {
  box-shadow: none;
  border: 1px solid @stroke-default;
  border-radius: 4px;
  padding: 0;
  background-color: @bg-default;
  min-width: 0;
}

.flsa-jurisdiction-select-menu .v-list-item {
  padding: 8px 12px;
  min-height: unset;
}

.flsa-jurisdiction-select-menu .v-list-item--disabled {
  opacity: 0.25;
}

.flsa-jurisdiction-select-menu .v-list-item-title {
  font-size: 13px;
  font-weight: 500;
  line-height: 150%;
  color: @text-dark;
}
</style>
